import { useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import dynamic from 'next/dynamic';

// Endpoints
import { COMMENT_MARK_AS_READ } from '@/lib/routes/apiEndpoints';

// Utils
import { post } from '@/utils/client';

// Lib
import { POST } from '@/lib/posts/postTypes';
import { FACEBOOK, INSTAGRAM } from '@/lib/socialProfile';
import { networkHasAccess } from '@/lib/acl/pageHasAccess';
import { MANAGE_COMMENTS_TRACKER } from '@/lib/routes/privateRoutes';

// Hooks
import useComments from '@/utils/hooks/swr/useComments';
import useActiveSocialProfile from '@/hooks/user/useActiveSocialProfile';

// Composites
import CommentsList from '@/components/composite/Comments/CommentsList';
const MediaViewerModal = dynamic(
    () => import('@/components/composite/MediaViewer/MediaViewerModal')
);

// Components
const InfoBanner = dynamic(() => import('@/components/base/InfoBanner'));
import { DropDownFilter } from '@/components/base/DropdownFilter/DropdownFilter';
import { ConversationsWrapper } from '@/components/layouts/wrappers/ConversationsWrapper';

const REFRESH_EVERY_MS = 30000; // 30 sec

const CommentsTrackerList = () => {
    const { t } = useTranslation('conversations');
    const { activeSocialProfile } = useActiveSocialProfile();
    const [activeFormReply, setActiveFormReply] = useState(null);
    const [markAllAsReadState, setMarkAllAsRead] = useState({
        markRepliesAsRead: false,
        isSubmitting: false,
        error: null
    });
    const [mediaViewerParams, setMediaViewerParams] = useState({
        isOpen: false,
        mediaId: null,
        mediaType: POST
    });

    const commentsFilters = [
        {
            name: 'conversations:comments_tracker.all_comments',
            status: null
        },
        {
            name: 'conversations:comments_tracker.unread_comments',
            status: 'unread'
        }
    ];

    const [commentFilter, setCommentsFilter] = useState(commentsFilters[0]);

    const {
        dataComments,
        isLoadingInitialData,
        isLoadMore,
        isLoadingMore,
        commentsError,
        mutateComments,
        loadMoreComments,
        setRequestParams,
        unreadCount
    } = useComments({
        activeSPID: activeSocialProfile?.id,
        shouldFetch: networkHasAccess[MANAGE_COMMENTS_TRACKER].includes(
            activeSocialProfile?.social_network_type
        ),
        options: {
            refreshInterval: REFRESH_EVERY_MS
        }
    });

    const handleMarkAllAsRead = async () => {
        setMarkAllAsRead((prevState) => ({
            ...prevState,
            isSubmitting: true,
            error: null
        }));

        try {
            await post(COMMENT_MARK_AS_READ.replace('%SPID%', activeSocialProfile?.id), {
                select: 'all'
            });
            mutateComments(
                (prevState) =>
                    prevState.map((page) => ({
                        ...page,
                        data: page.data.map((comment) => ({
                            ...comment,
                            is_read: true
                        })),
                        summary: {
                            ...page.summary,
                            unread_count: 0
                        }
                    })),
                false
            );
            setMarkAllAsRead((prevState) => ({
                ...prevState,
                isSubmitting: false,
                error: null,
                markRepliesAsRead: true
            }));
        } catch (e) {
            setMarkAllAsRead((prevState) => ({
                ...prevState,
                isSubmitting: false,
                error: true
            }));
        }
    };

    if (commentsError) {
        return (
            <InfoBanner
                title={t('errors:error')}
                message={t('errors:error_ocurred_fetch_comments')}
            />
        );
    }

    return (
        <ConversationsWrapper unreadComments={unreadCount}>
            <div className="flex h-full flex-1 flex-col pb-4">
                <DropDownFilter
                    classNameWrapper="manipulation-header relative z-2"
                    currentFilter={commentFilter}
                    filters={commentsFilters}
                    setFilter={(filter) => {
                        setCommentsFilter(filter);
                        setRequestParams(filter?.status ? { status: filter?.status } : null);
                    }}
                    buttonClassName="font-GBold text-xl"
                    className="text-base"
                    position="left"
                />

                <p className="mb-5 mt-3 text-icoGray-500 dark:text-icoDarkMode-wolf">
                    {t('comments_tracker.notice')}
                </p>
                <CommentsList
                    dataComments={dataComments}
                    socialProfile={activeSocialProfile}
                    isLoading={isLoadingInitialData}
                    isLoadingMore={isLoadingMore}
                    isLoadMore={isLoadMore}
                    handleMarkAllAsRead={handleMarkAllAsRead}
                    markAllAsReadState={markAllAsReadState}
                    setMarkAllAsRead={setMarkAllAsRead}
                    loadMore={loadMoreComments}
                    activeFormReply={activeFormReply}
                    setActiveFormReply={setActiveFormReply}
                    mutateComments={mutateComments}
                    displayPost={true}
                    onClickOnPost={(mediaId) => {
                        setMediaViewerParams({
                            isOpen: true,
                            mediaId: mediaId,
                            mediaType: POST
                        });
                    }}
                />
            </div>
            <MediaViewerModal
                isOpen={mediaViewerParams?.isOpen}
                toggle={() => {
                    setMediaViewerParams({ ...mediaViewerParams, isOpen: false });
                }}
                socialProfile={activeSocialProfile}
                mediaId={mediaViewerParams?.mediaId}
                mediaType={mediaViewerParams?.mediaType}
                mediaRequestParams={{
                    with_labels:
                        activeSocialProfile?.social_network_type === FACEBOOK ||
                        activeSocialProfile?.social_network_type === INSTAGRAM,
                    with_albums:
                        activeSocialProfile?.social_network_type === FACEBOOK ||
                        activeSocialProfile?.social_network_type === INSTAGRAM
                }}
                withNextPrev={false}
                defaultTabsIndex={1}
            />
        </ConversationsWrapper>
    );
};

export default CommentsTrackerList;
